<div class="px-2 py-3">
  <!-- Header -->
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div class="flex items-start gap-3">
      <span class="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">
        <lucide-icon [name]="MailIcon" class="h-5 w-5"></lucide-icon>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800 dark:text-white/90">Communications</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Every message across every contact, newest first. Click a line to read it.
        </p>
      </div>
    </div>

    <button
      type="button"
      data-testid="write-an-email"
      (click)="openCompose()"
      class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600">
      Write an email
    </button>
  </div>

  @if (sentNotice) {
    <div
      data-testid="sent-notice"
      class="mt-3 rounded-xl border border-green-200 bg-green-50/70 px-3 py-2 text-sm text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-300">
      {{ sentNotice }}
    </div>
  }

  <!-- Filters -->
  <div class="mt-4 grid gap-3 lg:grid-cols-4">
    <!-- Show: email or text -->
    <div>
      <span class="block text-sm font-medium text-gray-700 dark:text-gray-300">Show</span>
      <div class="mt-1 inline-flex overflow-hidden rounded-lg border border-gray-300 dark:border-gray-700" role="group" aria-label="Email or text">
        @for (choice of channelChoices; track choice.label) {
          <button
            type="button"
            [attr.aria-pressed]="channel === choice.value"
            [attr.data-testid]="'channel-' + (choice.value ?? 'all')"
            (click)="setChannel(choice.value)"
            [ngClass]="
              channel === choice.value
                ? 'bg-brand-500 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            "
            class="border-r border-gray-300 px-3 py-2 text-sm last:border-r-0 dark:border-gray-700">
            {{ choice.label }}
          </button>
        }
      </div>
    </div>

    <!-- Filter: sent, received or automatic -->
    <div>
      <span class="block text-sm font-medium text-gray-700 dark:text-gray-300">Filter</span>
      <div class="mt-1 inline-flex overflow-hidden rounded-lg border border-gray-300 dark:border-gray-700" role="group" aria-label="Kind of message">
        @for (choice of kindChoices; track choice.label) {
          <button
            type="button"
            [attr.aria-pressed]="kind === choice.value"
            [attr.data-testid]="'kind-' + (choice.value ?? 'all')"
            (click)="setKind(choice.value)"
            [ngClass]="
              kind === choice.value
                ? 'bg-brand-500 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
            "
            class="border-r border-gray-300 px-3 py-2 text-sm last:border-r-0 dark:border-gray-700">
            {{ choice.label }}
          </button>
        }
      </div>
    </div>

    <app-select
      label="Venue"
      placeholder="All venues"
      [control]="venueControl"
      [choices]="venueChoices"
      [searchable]="true"
      [clearable]="true"
    ></app-select>

    <app-text-input
      label="Search"
      name="search"
      placeholder="Contact, subject or words in the message"
      [control]="searchControl"
      [clearable]="true"
    ></app-text-input>
  </div>

  <!-- Count -->
  @if (hasLoadedOnce && !loadError && messages.length) {
    <div class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      Showing {{ messages.length }} of {{ total }} {{ total === 1 ? 'message' : 'messages' }}, newest first
    </div>
  }

  <!-- Error -->
  @if (loadError) {
    <div class="mt-4 flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50/60 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/[0.06] dark:text-red-300">
      <span>{{ loadError }}</span>
      <button type="button" (click)="retry()" class="shrink-0 font-medium underline">Try again</button>
    </div>
  }

  <!-- Empty state -->
  @if (isEmpty && !loadError) {
    <div
      data-testid="communications-empty"
      class="mt-4 rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-500 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-400">
      @if (isFiltered) {
        Nothing matches these filters.
      } @else {
        No messages have been recorded yet.
      }
    </div>
  }

  <!-- Lines: one message each -->
  @if (messages.length) {
    <div class="mt-4 overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
      <div class="hidden border-b border-gray-100 px-4 py-2 text-xs font-medium text-gray-500 sm:grid sm:grid-cols-12 sm:gap-4 dark:border-gray-800 dark:text-gray-400">
        <div class="sm:col-span-2">Received date</div>
        <div class="sm:col-span-2">From</div>
        <div class="sm:col-span-4">Message</div>
        <div class="sm:col-span-2">Kind</div>
        <div class="sm:col-span-2">Status</div>
      </div>

      @for (message of messages; track message.id) {
        <button
          type="button"
          data-testid="communication-row"
          (click)="openMessage(message)"
          class="block w-full border-b border-gray-100 px-4 py-3 text-left transition last:border-b-0 hover:bg-gray-50 sm:grid sm:grid-cols-12 sm:items-center sm:gap-4 dark:border-gray-800 dark:hover:bg-white/[0.06]">
          <div class="text-sm text-gray-600 sm:col-span-2 dark:text-gray-300">
            {{ message.sent_at | date: 'd MMM, HH:mm' }}
          </div>

          <div class="mt-1 sm:col-span-2 sm:mt-0">
            <div class="text-sm font-medium text-gray-800 dark:text-white/90">
              {{ message.contact?.name ?? message.recipient_email }}
            </div>
            @if (message.venue) {
              <div class="text-xs text-gray-500 dark:text-gray-400">{{ message.venue.name }}</div>
            }
          </div>

          <div class="mt-1 min-w-0 sm:col-span-4 sm:mt-0">
            <div class="flex items-center gap-2">
              @if (message.channel === 'text') {
                <span
                  data-testid="channel-badge-text"
                  class="inline-flex shrink-0 items-center rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-500/15 dark:text-purple-300">
                  Text
                </span>
              } @else {
                <span
                  data-testid="channel-badge-email"
                  class="inline-flex shrink-0 items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
                  Email
                </span>
              }
              <span class="truncate text-sm font-medium text-gray-800 dark:text-white/90">{{ message.subject }}</span>
            </div>
          </div>

          <div class="mt-2 text-xs text-gray-600 sm:col-span-2 sm:mt-0 dark:text-gray-300">
            @if (message.direction === 'inbound') {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600 dark:bg-white/5 dark:text-white/70">
                Received
              </span>
            } @else if (message.origin === 'automatic') {
              <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600 dark:bg-white/5 dark:text-white/70">
                Automatic
              </span>
            } @else {
              <span class="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 font-medium text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                Staff
              </span>
            }
            <span class="ml-1">{{ message.origin_label }}</span>
          </div>

          <div class="mt-2 sm:col-span-2 sm:mt-0">
            <span
              class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
              [ngClass]="statusClasses(message)">
              {{ message.status_label }}
            </span>
          </div>
        </button>
      }
    </div>
  }

  <!-- Scroll sentinel: fetches the next page as it comes into view. -->
  <div #scrollSentinel class="h-px"></div>

  @if (loading) {
    <div class="mt-3 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</div>
  }

  @if (!loading && !hasMore && messages.length) {
    <div class="mt-3 text-center text-xs text-gray-400 dark:text-gray-500">That's every message.</div>
  }
</div>

<!-- Message panel: reads one line in place, rather than opening a thread. -->
@if (selectedMessage) {
  <div class="fixed inset-0 z-[900]" data-testid="message-panel">
    <div class="absolute inset-0 bg-black/40" (click)="closeMessage()"></div>
    <aside
      class="absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
      <div class="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-800">
        <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">
          {{ selectedMessage.channel === 'text' ? 'Text message' : 'Email' }}
        </h3>
        <button
          type="button"
          class="text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
          (click)="closeMessage()"
          aria-label="Close message">
          <lucide-icon [name]="XIcon" [size]="18"></lucide-icon>
        </button>
      </div>

      <div class="flex-1 overflow-y-auto px-5 py-4">
        <h4 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ selectedMessage.subject }}</h4>

        <dl class="mt-3 grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
          <dt class="text-gray-500 dark:text-gray-400">From</dt>
          <dd class="col-span-2 text-gray-800 dark:text-white/90">
            @if (selectedMessage.direction === 'inbound') {
              {{ selectedMessage.contact?.name ?? selectedMessage.recipient_name ?? selectedMessage.recipient_email }}
              @if (selectedMessage.contact) {
                ({{ selectedMessage.recipient_email }})
              }
            } @else if (selectedMessage.sent_by) {
              {{ selectedMessage.sent_by.name }}@if (selectedMessage.venue) {, as {{ selectedMessage.venue.name }}}
            } @else {
              The system@if (selectedMessage.venue) {, as {{ selectedMessage.venue.name }}}
            }
          </dd>

          <dt class="text-gray-500 dark:text-gray-400">To</dt>
          <dd class="col-span-2 text-gray-800 dark:text-white/90">
            @if (selectedMessage.direction === 'inbound') {
              The Venue Group
            } @else {
              {{ selectedMessage.contact?.name ?? '—' }} ({{ selectedMessage.recipient_email }})
            }
          </dd>

          <dt class="text-gray-500 dark:text-gray-400">When</dt>
          <dd class="col-span-2 text-gray-800 dark:text-white/90">
            {{ selectedMessage.sent_at | date: 'd MMM y, HH:mm' }}
          </dd>

          <dt class="text-gray-500 dark:text-gray-400">Kind</dt>
          <dd class="col-span-2 text-gray-800 dark:text-white/90">{{ selectedMessage.origin_label }}</dd>

          <dt class="text-gray-500 dark:text-gray-400">Status</dt>
          <dd class="col-span-2">
            <span
              class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
              [ngClass]="statusClasses(selectedMessage)">
              {{ selectedMessage.status_label }}
            </span>
            @if (selectedMessage.status === 'failed') {
              <p class="mt-1 text-xs text-red-600 dark:text-red-400">
                The mail server did not accept it. Nothing reached the contact.
              </p>
            }
            @if (selectedMessage.status === 'queued') {
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Waiting to go out. It sends in the background.
              </p>
            }
            @if (selectedMessage.status === 'bounced') {
              <p class="mt-1 text-xs text-red-600 dark:text-red-400">
                It bounced. The contact's mail server refused it.
              </p>
            }
            @if (selectedMessage.status === 'not_filed') {
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid="not-filed-hint">
                @if (selectedMessage.failure_reason) {
                  {{ selectedMessage.failure_reason }}
                } @else {
                  No contact holds {{ selectedMessage.recipient_email }}. Add it to the right contact as an extra email
                  address and this email is filed there, along with any future email from it.
                }
              </p>
            }
          </dd>
        </dl>

        @if (selectedMessage.body) {
          <div class="mt-4 whitespace-pre-wrap rounded-xl bg-gray-50 p-4 text-sm text-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
            {{ selectedMessage.body }}
          </div>
        } @else {
          <p class="mt-4 rounded-xl bg-gray-50 p-4 text-sm text-gray-500 dark:bg-gray-800/60 dark:text-gray-400">
            This is one of the emails the platform sends from its own templates, so only what it was and when it went
            are recorded here.
          </p>
        }

        @if (resendError) {
          <p class="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
            {{ resendError }}
          </p>
        }

        <div class="mt-4 flex flex-wrap items-center gap-2">
          @if (selectedMessage.can_resend) {
            <button
              type="button"
              data-testid="send-again"
              [disabled]="resending"
              (click)="resend(selectedMessage)"
              class="inline-flex items-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60">
              {{ resending ? 'Sending…' : 'Send again' }}
            </button>
          }

          @if (selectedMessage.contact) {
            <a
              [routerLink]="['/contacts', selectedMessage.contact.id]"
              class="inline-flex items-center rounded-lg border border-brand-500 px-4 py-2 text-sm font-medium text-brand-600 transition hover:bg-brand-50 dark:text-brand-400 dark:hover:bg-brand-500/10">
              Open the contact
            </a>
          }
        </div>

        <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Recorded when it was sent, so it is here and on the contact page.
        </p>
      </div>
    </aside>
  </div>
}

<app-compose-email-modal
  [isOpen]="composeOpen"
  (sent)="onSent($event)"
  (cancelled)="closeCompose()"
></app-compose-email-modal>
